// 매장 운영설정 / 사용자 주소록 공통 타입과 zod 스키마.
// 사용자 앱(주소록, 영업안내 위젯, 주문 검증), 관리자 콘솔(운영설정 폼), API 가 모두 본 파일을 공유한다.

import { z } from "zod";
import { phoneNumberKrSchema } from "./schemas.js";
import { couponSpecSchema, type CouponSpec } from "./coupon.js";

// 시간 문자열 'HH:MM' (00:00 ~ 23:59) 검증.
//  - 24:00 같은 표기는 받지 않는다. "마감이 자정 직후" 인 경우는 다음 날 영업시간으로 처리해야 한다.
export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/u, "시간 형식은 HH:MM 이어야 합니다.");

// YYYY-MM-DD 날짜 문자열 검증. JS Date 로 다시 파싱 가능한지도 확인한다.
export const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, "날짜 형식은 YYYY-MM-DD 이어야 합니다.")
  .refine((s) => !Number.isNaN(new Date(`${s}T00:00:00Z`).getTime()), {
    message: "유효한 날짜가 아닙니다.",
  });

// 요일 키. 사용자 앱/관리자 콘솔에서 동일 키로 다룬다.
export const WEEKDAY = {
  MON: "mon",
  TUE: "tue",
  WED: "wed",
  THU: "thu",
  FRI: "fri",
  SAT: "sat",
  SUN: "sun",
} as const;
export type Weekday = (typeof WEEKDAY)[keyof typeof WEEKDAY];

export const WEEKDAYS_ORDERED: ReadonlyArray<Weekday> = [
  WEEKDAY.MON,
  WEEKDAY.TUE,
  WEEKDAY.WED,
  WEEKDAY.THU,
  WEEKDAY.FRI,
  WEEKDAY.SAT,
  WEEKDAY.SUN,
];

export const WEEKDAY_LABEL_KO: Record<Weekday, string> = {
  mon: "월",
  tue: "화",
  wed: "수",
  thu: "목",
  fri: "금",
  sat: "토",
  sun: "일",
};

// 요일별 영업시간 1행 (null 이면 정기 휴무).
// open < close 관계를 zod 단계에서 검증한다.
//  - 자정 넘어가는 영업 ("22:00 ~ 02:00") 은 MVP 정육점 운영 가정상 발생 안한다고 가정하고 허용 안함.
export const businessHourSchema = z
  .object({
    open: timeOfDaySchema,
    close: timeOfDaySchema,
  })
  .refine((v) => v.open < v.close, {
    message: "마감 시각은 시작 시각보다 뒤여야 합니다.",
    path: ["close"],
  });
export type BusinessHour = z.infer<typeof businessHourSchema>;

// 요일별 영업시간 맵.
// 누락된 요일은 null(휴무) 로 간주한다.
export const weeklyHoursSchema = z.object({
  mon: businessHourSchema.nullable(),
  tue: businessHourSchema.nullable(),
  wed: businessHourSchema.nullable(),
  thu: businessHourSchema.nullable(),
  fri: businessHourSchema.nullable(),
  sat: businessHourSchema.nullable(),
  sun: businessHourSchema.nullable(),
});
export type WeeklyHours = z.infer<typeof weeklyHoursSchema>;

// 허용 슬롯 단위(분). DB CHECK 와 1:1 대응.
export const RESERVATION_SLOT_MINUTES_OPTIONS = [15, 30, 60] as const;
export type ReservationSlotMinutes = (typeof RESERVATION_SLOT_MINUTES_OPTIONS)[number];

// 회원 등급 1개 규칙. key 는 내부 식별자(slug), name 은 표시 이름, minSpendKrw 는 부여 기준(누적 구매액).
//  - coupons: 이 등급 회원에게 매월 발행할 등급 쿠폰팩 (관리자 설정). 비어 있으면 미발행.
export const tierRuleSchema = z.object({
  key: z.string().trim().min(1).max(30).regex(/^[a-z0-9_-]+$/u, "등급 키는 영소문자/숫자/-/_ 만 가능합니다."),
  name: z.string().trim().min(1).max(20),
  minSpendKrw: z.number().int().min(0),
  coupons: z.array(couponSpecSchema).max(10).default([]),
});
export type TierRule = z.infer<typeof tierRuleSchema>;

// 관리자: 매장 운영설정 입력 (PUT 전체 갱신).
//  - 부분 갱신 대신 화면에서 항상 전체 폼을 제출하는 것을 가정해 간단히 둔다.
//  - delivery_enabled = false 면 키워드/최소금액/배달비는 무시된다 (서버는 입력값을 그대로 저장).
export const storeSettingsInputSchema = z.object({
  weeklyHours: weeklyHoursSchema,
  closedDates: z.array(calendarDateSchema).max(366),
  orderWindowOpen: timeOfDaySchema.nullable(),
  orderWindowClose: timeOfDaySchema.nullable(),
  deliveryEnabled: z.boolean(),
  // 배달 주문 가능 시간(개선 2). 설정 시 이 시간대에만 배달 주문/예약 배달이 가능(포장은 무관). 둘 다 null 이면 제한 없음.
  deliveryWindowOpen: timeOfDaySchema.nullable(),
  deliveryWindowClose: timeOfDaySchema.nullable(),
  deliveryAreaKeywords: z.array(z.string().trim().min(1).max(40)).max(100),
  deliveryMinOrderKrw: z.number().int().min(0),
  deliveryFeeKrw: z.number().int().min(0),
  // 다음날 배달 전용. 켜면 배달 주문은 시간 선택 없이 "보냉 포장 후 다음날 오전 배송"으로 고정된다.
  //  - 배달 주문 가능 시간(deliveryWindow)은 무시된다. 최소금액/지역키워드는 그대로 적용. 포장은 무관.
  nextDayDeliveryOnly: z.boolean(),
  // 칭찬하고 할인받기(개선 5~12).
  complimentEnabled: z.boolean(),
  // 포장/배달 주문 시 무작위로 보여줄 칭찬 개수.
  complimentPickupCount: z.number().int().min(0).max(20),
  complimentDeliveryCount: z.number().int().min(0).max(20),
  // 칭찬 할인 범위(원)와 단위(원). 단위 배수로 min~max 중 가중 랜덤 1개.
  complimentDiscountMinKrw: z.number().int().min(0).max(1_000_000),
  complimentDiscountMaxKrw: z.number().int().min(0).max(1_000_000),
  complimentDiscountUnitKrw: z.number().int().min(1).max(1_000_000),
  // 칭찬 문구 목록.
  complimentList: z.array(z.string().trim().min(1).max(50)).max(100),
  noticeText: z.string().max(2000).nullable(),
  // 예약 주문 정책.
  reservationEnabled: z.boolean(),
  reservationMaxDaysAhead: z.number().int().min(0).max(60),
  reservationMinLeadMinutes: z.number().int().min(0).max(1440),
  reservationSlotMinutes: z.union([z.literal(15), z.literal(30), z.literal(60)]),
  // 적립금 정책 (Phase 4-A). pointsEnabled=false 또는 percent=0 이면 적립하지 않는다.
  //  - 적립은 수령완료 시점에 주문 합계(total) 대비 percent% 로 확정된다.
  pointsEnabled: z.boolean(),
  // 적립률은 포장/배달 주문별로 분리한다(개선). pointsEarnPercent = 포장, pointsEarnPercentDelivery = 배달.
  pointsEarnPercent: z.number().int().min(0).max(100),
  pointsEarnPercentDelivery: z.number().int().min(0).max(100),
  // 적립금 사용 정책(개선항목 16). 최소 사용 기준(이 금액 이상부터 사용 가능) + 사용 단위(이 단위의 배수로만).
  //  - pointsMinUseKrw=0 이면 최소 제한 없음, pointsUseUnitKrw=1 이면 1원 단위(사실상 제한 없음).
  pointsMinUseKrw: z.number().int().min(0),
  pointsUseUnitKrw: z.number().int().min(1),
  // 회원 등급 정책 (Phase 4-B). 직전 N개월 완료 실적 기준. tierEnabled=false 또는 tiers 비어있으면 기본 등급만.
  tierEnabled: z.boolean(),
  tiers: z.array(tierRuleSchema).max(10),
  // 등급 산정 윈도우(직전 개월 수). 예: 3 이면 현재 달 제외 직전 3개 달력월 합계로 산정.
  tierWindowMonths: z.number().int().min(1).max(24),
  // 가입 시 자동 발급할 쿠폰 팩 (관리자 설정). 비어 있으면 가입 쿠폰 미발급.
  signupCoupons: z.array(couponSpecSchema).max(10),
  // 1:1 문의 연결 (전화/카카오 채널). 비어 있으면 사용자 화면에서 해당 버튼 숨김.
  contactPhone: z.string().trim().max(20).nullable(),
  kakaoUrl: z.string().trim().max(300).nullable(),
  // 첫 진입 스플래시(로딩) 이미지 R2 키(개선항목 18). null 이면 기본 브랜드 스플래시를 표시.
  splashImageKey: z.string().trim().max(300).nullable(),
})
.superRefine((v, ctx) => {
  // 주문 가능 시간이 둘 다 채워졌으면 open < close 강제.
  if (v.orderWindowOpen && v.orderWindowClose && v.orderWindowOpen >= v.orderWindowClose) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["orderWindowClose"],
      message: "주문 마감 시각은 시작 시각보다 뒤여야 합니다.",
    });
  }
  // 둘 중 하나만 채워진 경우도 둘 다 채우도록 강제 (혼동 방지).
  if ((!!v.orderWindowOpen) !== (!!v.orderWindowClose)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["orderWindowClose"],
      message: "주문 가능 시간은 시작/마감을 함께 비우거나 함께 채워야 합니다.",
    });
  }
  // 배달 주문 가능 시간도 동일 규칙(둘 다 채우거나 비우고, open < close).
  if (v.deliveryWindowOpen && v.deliveryWindowClose && v.deliveryWindowOpen >= v.deliveryWindowClose) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["deliveryWindowClose"],
      message: "배달 마감 시각은 시작 시각보다 뒤여야 합니다.",
    });
  }
  if ((!!v.deliveryWindowOpen) !== (!!v.deliveryWindowClose)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["deliveryWindowClose"],
      message: "배달 주문 가능 시간은 시작/마감을 함께 비우거나 함께 채워야 합니다.",
    });
  }
  // 칭찬 할인 범위: 최대 >= 최소.
  if (v.complimentDiscountMaxKrw < v.complimentDiscountMinKrw) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["complimentDiscountMaxKrw"],
      message: "칭찬 할인 최대 금액은 최소 금액 이상이어야 합니다.",
    });
  }
});
export type StoreSettingsInput = z.infer<typeof storeSettingsInputSchema>;

// 응답 DTO (관리자/사용자 공통). 사용자 화면에는 일부 필드만 노출하도록 라우트에서 선택.
export interface StoreSettingsDto {
  weeklyHours: WeeklyHours;
  closedDates: string[];
  orderWindowOpen: string | null;
  orderWindowClose: string | null;
  deliveryEnabled: boolean;
  deliveryWindowOpen: string | null;
  deliveryWindowClose: string | null;
  deliveryAreaKeywords: string[];
  deliveryMinOrderKrw: number;
  deliveryFeeKrw: number;
  // 다음날 배달 전용(보냉 포장 후 익일 오전 배송). 켜면 배달 주문은 수령 시간 선택이 없다.
  nextDayDeliveryOnly: boolean;
  // 칭찬하고 할인받기(개선 5~12). complimentList 는 공개 응답에선 제외하고, 무작위 노출은 /me/compliment 가 담당.
  complimentEnabled: boolean;
  complimentPickupCount: number;
  complimentDeliveryCount: number;
  complimentDiscountMinKrw: number;
  complimentDiscountMaxKrw: number;
  complimentDiscountUnitKrw: number;
  complimentList: string[];
  noticeText: string | null;
  reservationEnabled: boolean;
  reservationMaxDaysAhead: number;
  reservationMinLeadMinutes: number;
  reservationSlotMinutes: ReservationSlotMinutes;
  pointsEnabled: boolean;
  pointsEarnPercent: number;
  pointsEarnPercentDelivery: number;
  pointsMinUseKrw: number;
  pointsUseUnitKrw: number;
  tierEnabled: boolean;
  tiers: TierRule[];
  tierWindowMonths: number;
  signupCoupons: CouponSpec[];
  contactPhone: string | null;
  kakaoUrl: string | null;
  splashImageKey: string | null;
  updatedAt: string;
}

// 사용자에게 노출되는 공개 DTO. 관리자 ID 등 운영 메타는 제외하고 동일 필드를 그대로 노출한다.
// 별도 타입을 두지 않고 StoreSettingsDto 를 재사용한다.

// 적립금 사용 정책(개선항목 16).
export interface PointsUsePolicy {
  // 최소 사용 기준(원). 이 금액 이상부터 사용 가능. 0 이면 제한 없음.
  minKrw: number;
  // 사용 단위(원). 사용 금액은 이 값의 배수여야 한다. 1 이면 제한 없음.
  unitKrw: number;
}

// DTO 에서 적립금 사용 정책을 추출한다.
export function pointsUsePolicyOf(settings: StoreSettingsDto): PointsUsePolicy {
  return {
    minKrw: settings.pointsMinUseKrw,
    unitKrw: settings.pointsUseUnitKrw > 0 ? settings.pointsUseUnitKrw : 1,
  };
}

// 사용하려는 적립금 액수가 정책(최소사용/단위)상 유효한지.
//  - 0(사용 안 함)은 항상 허용.
//  - 그 외에는 최소 기준 이상 + 단위의 배수여야 한다.
export function isPointsUseAmountValid(amount: number, policy: PointsUsePolicy): boolean {
  if (amount <= 0) {
    return amount === 0;
  }
  if (amount < policy.minKrw) {
    return false;
  }
  const unit = policy.unitKrw > 0 ? policy.unitKrw : 1;
  return amount % unit === 0;
}

// 보유/결제가능 한도 안에서 정책에 맞는 최대 사용 가능액(전액 사용 버튼/상한 표시용).
//  - 한도가 최소 기준에 못 미치면 0(사용 불가).
export function maxUsablePoints(balance: number, payable: number, policy: PointsUsePolicy): number {
  const cap = Math.min(Math.max(0, balance), Math.max(0, payable));
  if (cap < policy.minKrw) {
    return 0;
  }
  const unit = policy.unitKrw > 0 ? policy.unitKrw : 1;
  const floored = Math.floor(cap / unit) * unit;
  return floored >= policy.minKrw ? floored : 0;
}

// 주소 입력. 사용자/관리자 모두 동일 필드를 사용한다.
const ADDR_LINE_MAX = 100;
const ADDR_LABEL_MAX = 30;
const ADDR_RECIPIENT_MAX = 30;
const POSTAL_MAX = 10;

export const addressInputSchema = z.object({
  label: z.string().trim().max(ADDR_LABEL_MAX).nullable().optional(),
  recipientName: z.string().trim().min(1, "받는 분 이름을 입력하세요.").max(ADDR_RECIPIENT_MAX),
  recipientPhone: phoneNumberKrSchema,
  addressLine1: z.string().trim().min(1, "주소를 입력하세요.").max(ADDR_LINE_MAX),
  addressLine2: z.string().trim().max(ADDR_LINE_MAX).nullable().optional(),
  postalCode: z.string().trim().max(POSTAL_MAX).nullable().optional(),
  isDefault: z.boolean().optional(),
});
export type AddressInput = z.infer<typeof addressInputSchema>;

export const addressUpdateSchema = addressInputSchema.partial();
export type AddressUpdate = z.infer<typeof addressUpdateSchema>;

export interface AddressDto {
  id: string;
  label: string | null;
  recipientName: string;
  recipientPhone: string;
  addressLine1: string;
  addressLine2: string | null;
  postalCode: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

// 사용자 주소 텍스트가 배달 가능 키워드 중 하나에 포함되는지 검사한다.
// 검사 규칙:
//  - line1 + line2 를 공백으로 연결한 합본 문자열 안에서 키워드를 includes 검사한다.
//  - 키워드 자체에 공백이 있으면 그대로 부분 일치 (예: '강남구 도곡동').
//  - 키워드 목록이 비어 있으면 false (배달 불가) 가 아니라 true 를 반환한다 - "지역 제한 미설정" 의미.
export function isDeliveryAddressAllowed(
  fullAddress: string,
  keywords: ReadonlyArray<string>,
): boolean {
  if (keywords.length === 0) {
    return true;
  }
  const text = fullAddress.trim();
  for (const k of keywords) {
    const kt = k.trim();
    if (kt && text.includes(kt)) {
      return true;
    }
  }
  return false;
}

// JS Date 객체를 시스템 타임존(서버는 UTC, 클라이언트는 KST)을 무시하고 안전하게
// (year, weekday, HH:MM) 으로 분해한 결과를 반환한다.
// 매장 영업 판단은 매장 로컬 타임존 기준이 자연스러우므로 호출자가 timezone 변환 후 본 함수에 넘긴다.
//  - 본 함수는 변환된 값들로부터 weekday/HH:MM 만 추출해 store_settings 와 비교용으로 가공한다.
export interface DateTimeBreakdown {
  yyyyMmDd: string;
  weekday: Weekday;
  hhmm: string;
}

// JS Date.getDay() 0(일)..6(토) -> Weekday enum 매핑.
const JS_WEEKDAY_TO_ENUM: ReadonlyArray<Weekday> = [
  WEEKDAY.SUN,
  WEEKDAY.MON,
  WEEKDAY.TUE,
  WEEKDAY.WED,
  WEEKDAY.THU,
  WEEKDAY.FRI,
  WEEKDAY.SAT,
];

// Date 객체에서 매장 로컬 기준 weekday/HH:MM/YYYY-MM-DD 분해.
// 본 함수는 입력 Date 가 이미 매장 로컬 시각으로 해석되는 가정에서 동작한다.
//   호출자가 Intl.DateTimeFormat 등으로 로컬 변환 후 넘기는 것이 표준 경로.
export function breakdownDate(local: Date): DateTimeBreakdown {
  const yyyy = String(local.getFullYear()).padStart(4, "0");
  const mm = String(local.getMonth() + 1).padStart(2, "0");
  const dd = String(local.getDate()).padStart(2, "0");
  const hh = String(local.getHours()).padStart(2, "0");
  const min = String(local.getMinutes()).padStart(2, "0");
  const weekday = JS_WEEKDAY_TO_ENUM[local.getDay()] ?? WEEKDAY.MON;
  return { yyyyMmDd: `${yyyy}-${mm}-${dd}`, weekday, hhmm: `${hh}:${min}` };
}

// 매장이 지금 영업 중인지 판단한다.
// 입력:
//  - settings: 운영설정 DTO
//  - now: 매장 로컬 기준 현재 시각
// 출력:
//  - isOpen: 현재 영업/주문 가능 여부 (영업시간 ∩ 주문가능시간 ∩ 휴무 아님)
//  - reason: 닫혀 있다면 그 이유 코드
export interface OpenStatus {
  isOpen: boolean;
  // 지금 시각이 매장 영업시간 안인지 (주문 가능 시간과 별개).
  isWithinBusinessHour: boolean;
  // 지금 시각이 주문 가능 시간 안인지 (영업시간과 별개).
  isWithinOrderWindow: boolean;
  // 오늘이 휴무일이거나 정기 휴무 요일이면 true.
  isClosedToday: boolean;
  // 닫힌 사유 (사용자 안내용 - 영어 코드, 라벨은 클라이언트에서 한글화).
  reason: "open" | "closed-date" | "regular-off" | "before-open" | "after-close" | "order-window";
}

// 현재 시각 기준 매장 영업 상태 계산. 휴무일/정기휴무/영업시간/주문가능창을 순서대로 검증.
export function computeOpenStatus(settings: StoreSettingsDto, now: Date): OpenStatus {
  const { yyyyMmDd, weekday, hhmm } = breakdownDate(now);
  const isClosedDate = settings.closedDates.includes(yyyyMmDd);
  const hoursToday = settings.weeklyHours[weekday];
  const isRegularOff = hoursToday === null;
  const isClosedToday = isClosedDate || isRegularOff;

  let isWithinBusinessHour = false;
  if (hoursToday && !isClosedDate) {
    isWithinBusinessHour = hoursToday.open <= hhmm && hhmm < hoursToday.close;
  }

  // 주문 가능 시간 미설정이면 영업시간을 그대로 따른다.
  let isWithinOrderWindow = isWithinBusinessHour;
  if (settings.orderWindowOpen && settings.orderWindowClose && !isClosedDate && !isRegularOff) {
    isWithinOrderWindow =
      settings.orderWindowOpen <= hhmm && hhmm < settings.orderWindowClose;
  }

  const isOpen = !isClosedToday && isWithinBusinessHour && isWithinOrderWindow;

  let reason: OpenStatus["reason"] = "open";
  if (isClosedDate) {
    reason = "closed-date";
  } else if (isRegularOff) {
    reason = "regular-off";
  } else if (!isWithinBusinessHour && hoursToday) {
    reason = hhmm < hoursToday.open ? "before-open" : "after-close";
  } else if (!isWithinOrderWindow) {
    reason = "order-window";
  }
  return { isOpen, isWithinBusinessHour, isWithinOrderWindow, isClosedToday, reason };
}

// 사용자 화면용 사유 라벨 (손님 카피 톤 - 부정적 표현 회피).
export const OPEN_STATUS_REASON_LABEL_KO: Record<OpenStatus["reason"], string> = {
  "open": "지금 주문 가능합니다.",
  "closed-date": "오늘은 휴무입니다.",
  "regular-off": "오늘은 휴무입니다.",
  "before-open": "영업 시작 전입니다.",
  "after-close": "영업이 종료되었습니다.",
  "order-window": "지금은 주문 시간이 아닙니다.",
};

// 예약 주문 슬롯.
// 사용자 카트/주문 화면에서 일자별로 그루핑해 노출한다.
//  - yyyyMmDd : 매장 로컬 기준 날짜
//  - weekday  : 해당 요일 (영업시간 라벨 표시용)
//  - slots    : 'HH:MM' 시작 시각 배열 (선택 시 그 시각에 수령)
export interface ReservationDay {
  yyyyMmDd: string;
  weekday: Weekday;
  slots: string[];
}

// HH:MM 시각을 0..1440 분으로 환산.
function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":");
  return (Number.parseInt(h ?? "0", 10) || 0) * 60 + (Number.parseInt(m ?? "0", 10) || 0);
}

// 분을 HH:MM 으로 환산.
function minutesToHhmm(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// 매장 로컬 기준 일자를 N일 이동한 YYYY-MM-DD 와 weekday 를 반환한다.
function shiftDate(base: Date, days: number): { yyyyMmDd: string; weekday: Weekday } {
  const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
  const { yyyyMmDd, weekday } = breakdownDate(next);
  return { yyyyMmDd, weekday };
}

// 예약 가능 슬롯 목록 생성.
// 입력:
//  - settings: 운영 설정 (예약 정책 포함)
//  - now: 매장 로컬 현재 시각
// 출력:
//  - 일자별로 그루핑된 슬롯. 오늘은 reservation_min_lead_minutes 만큼의 미래 슬롯부터만 포함된다.
//  - reservationEnabled = false 인 경우 빈 배열.
//
// 슬롯 결정 규칙:
//  - 해당 일자의 weeklyHours[weekday] 가 null 이거나 closedDates 에 포함되면 슬롯 없음.
//  - 그 외에는 영업시간(open..close) 안의 시작 시각을 slotMinutes 단위로 정렬.
//  - 주문가능시간(orderWindow) 이 별도 설정된 경우 그 범위로 더 좁힌다.
//  - 마지막 슬롯은 close 보다 작아야 한다 (close 정각 시작은 제외).
export function listReservationSlots(settings: StoreSettingsDto, now: Date): ReservationDay[] {
  if (!settings.reservationEnabled) {
    return [];
  }
  const result: ReservationDay[] = [];
  const slotMin = settings.reservationSlotMinutes;
  const leadMin = settings.reservationMinLeadMinutes;
  const earliestThreshold = new Date(now.getTime() + leadMin * 60_000);
  for (let dayOffset = 0; dayOffset <= settings.reservationMaxDaysAhead; dayOffset += 1) {
    const { yyyyMmDd, weekday } = shiftDate(now, dayOffset);
    if (settings.closedDates.includes(yyyyMmDd)) {
      continue;
    }
    const hours = settings.weeklyHours[weekday];
    if (!hours) {
      continue;
    }
    // 주문가능시간 우선, 미설정 시 영업시간 사용.
    const openMin = hhmmToMinutes(
      settings.orderWindowOpen && settings.orderWindowClose ? settings.orderWindowOpen : hours.open,
    );
    const closeMin = hhmmToMinutes(
      settings.orderWindowOpen && settings.orderWindowClose
        ? settings.orderWindowClose
        : hours.close,
    );
    // 일자 기준 자정에서 분 단위로 시작점을 잡고 slotMinutes 단위로 증가.
    // openMin 이 slotMinutes 배수가 아니면 다음 배수로 끌어올린다.
    const firstSlot = Math.ceil(openMin / slotMin) * slotMin;
    const slots: string[] = [];
    for (let m = firstSlot; m < closeMin; m += slotMin) {
      // 해당 슬롯의 실제 Date (매장 로컬 기준) 를 만들어 lead 임계와 비교.
      const slotDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + dayOffset,
        Math.floor(m / 60),
        m % 60,
        0,
        0,
      );
      if (slotDate.getTime() < earliestThreshold.getTime()) {
        continue;
      }
      slots.push(minutesToHhmm(m));
    }
    if (slots.length > 0) {
      result.push({ yyyyMmDd, weekday, slots });
    }
  }
  return result;
}

// 사용자가 선택한 (yyyyMmDd, HH:MM) 슬롯이 운영설정 정책상 유효한지 검증한다.
// 라우트에서 주문 생성 직전에 한 번 더 호출해 클라이언트 신뢰를 끊는다.
export function isReservationSlotValid(
  settings: StoreSettingsDto,
  now: Date,
  yyyyMmDd: string,
  hhmm: string,
): boolean {
  const days = listReservationSlots(settings, now);
  const day = days.find((d) => d.yyyyMmDd === yyyyMmDd);
  if (!day) {
    return false;
  }
  return day.slots.includes(hhmm);
}

// 배달 주문 가능 시간(개선 2) 안인지. 시작/마감이 비어 있으면(미설정) 항상 true(제한 없음).
export function isWithinDeliveryWindow(settings: StoreSettingsDto, hhmm: string): boolean {
  if (!settings.deliveryWindowOpen || !settings.deliveryWindowClose) {
    return true;
  }
  return settings.deliveryWindowOpen <= hhmm && hhmm < settings.deliveryWindowClose;
}

// 지금 배달 ASAP 주문이 가능한지: 매장이 주문 가능 상태(영업+주문창) + 배달 시간대 안.
export function isDeliveryOrderableNow(settings: StoreSettingsDto, now: Date): boolean {
  if (!computeOpenStatus(settings, now).isOpen) {
    return false;
  }
  return isWithinDeliveryWindow(settings, breakdownDate(now).hhmm);
}

// 예약 슬롯 목록을 배달 시간대로 좁힌다(배달 주문 선택 시 사용). 미설정이면 그대로 반환.
export function narrowReservationDaysToDeliveryWindow(
  days: ReservationDay[],
  settings: StoreSettingsDto,
): ReservationDay[] {
  if (!settings.deliveryWindowOpen || !settings.deliveryWindowClose) {
    return days;
  }
  return days
    .map((d) => ({ ...d, slots: d.slots.filter((s) => isWithinDeliveryWindow(settings, s)) }))
    .filter((d) => d.slots.length > 0);
}
